import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { getParametrizedHotelContext, getParametrizedHotelInfoContext } from 'middleware/analytics/selectors';
import { getCurrencyCode, getIsRtlLanguage } from 'modules/meta/selectors';
import { getIsHotelVisited } from 'modules/search/selectors';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { Icon } from '@daedalus/atlas/Icon';
import { cssTheme } from '@daedalus/atlas/themes';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, AnalyticsContext, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import { applyRoundingStrategy, getRoundingStrategy } from '@daedalus/core/src/utils/number';
enum MarkerState {
  Default,
  Selected,
  Visited,
}
const getMarkerState = (isSelected: boolean, isVisited: boolean) => {
  if (isSelected) {
    return MarkerState.Selected;
  }
  if (isVisited) {
    return MarkerState.Visited;
  }
  return MarkerState.Default;
};
interface Props extends Partial<Pick<React.HTMLProps<HTMLDivElement>, 'onMouseEnter' | 'onMouseLeave'>> {
  id: string;
  price: number;
  isSelected: boolean;
  isAnchor?: boolean;
  onClick: (hotelId: string) => void;
  isPrivateDeal: boolean;
  hotelNameSlot?: React.ReactNode;
  distanceSlot?: React.ReactNode;
}
type MarkerStyleProps = {
  isRTL: boolean;
  borderColor: string;
  backgroundColor: string;
  textColor: string;
};
const getMarkerBackground = (markerState: MarkerState, isPrivateDeal: boolean, brandShowSignIn: boolean) => {
  switch (markerState) {
    case MarkerState.Visited:
      return cssTheme.colors.background.neutral.c100;
    case MarkerState.Selected:
      return isPrivateDeal && brandShowSignIn ? cssTheme.colors.price.special : cssTheme.colors.mapPins.selected.background;
    default:
      return cssTheme.colors.background.neutral.c000;
  }
};
const getMarkerColor = (markerState: MarkerState, isPrivateDeal: boolean, brandShowSignIn: boolean) => {
  switch (markerState) {
    case MarkerState.Selected:
      return cssTheme.colors.content.neutral.c000;
    default:
      return isPrivateDeal && brandShowSignIn ? cssTheme.colors.price.special : undefined;
  }
};
const getMarkerBorderColor = (markerState: MarkerState, isAnchor: boolean) => {
  if (isAnchor) {
    return cssTheme.colors.mapPins.special.border;
  }
  if (markerState === MarkerState.Visited) {
    return cssTheme.colors.border.neutral.c200;
  }
  return cssTheme.colors.transparent;
};
const _exp8 = /*#__PURE__*/() => ({
  textColor
}) => textColor;
const _exp15 = /*#__PURE__*/() => ({
  isRTL
}) => isRTL ? '0 2px' : '2px 0';
const _exp17 = /*#__PURE__*/() => ({
  backgroundColor
}) => backgroundColor;
const _exp18 = /*#__PURE__*/() => ({
  borderColor,
  isRTL
}) => isRTL ? 'initial' : `1px solid ${borderColor}`;
const _exp19 = /*#__PURE__*/() => ({
  borderColor,
  isRTL
}) => isRTL ? `1px solid ${borderColor}` : 'initial';
const _exp20 = /*#__PURE__*/() => ({
  borderColor
}) => `1px solid ${borderColor}`;
const Marker = /*#__PURE__*/styled('div')({
  name: "Marker",
  class: "mrtcsw6",
  propsAsIs: false,
  vars: {
    "mrtcsw6-0": [_exp17()],
    "mrtcsw6-1": [_exp8()],
    "mrtcsw6-2": [_exp20()],
    "mrtcsw6-3": [_exp15()],
    "mrtcsw6-4": [_exp18()],
    "mrtcsw6-5": [_exp19()]
  }
});
const MarkerContent = /*#__PURE__*/styled('div')({
  name: "MarkerContent",
  class: "mx7qycq",
  propsAsIs: false
});
const MarkerWrapper = /*#__PURE__*/styled('div')({
  name: "MarkerWrapper",
  class: "mewcwh8",
  propsAsIs: false
});
const Price = /*#__PURE__*/styled('div')({
  name: "Price",
  class: "pu55bkn",
  propsAsIs: false
});
const _exp23 = /*#__PURE__*/() => Flex.Row;
const _exp26 = /*#__PURE__*/() => ({
  isSelected
}) => isSelected ? cssTheme.colors.content.neutral.c000 : cssTheme.colors.content.danger.c500;
const IconWrapper = /*#__PURE__*/styled(_exp23())({
  name: "IconWrapper",
  class: "i1u85vph",
  propsAsIs: true,
  vars: {
    "i1u85vph-0": [_exp26()]
  }
});
export const HotelMarkerContent = ({
  id,
  price,
  isSelected,
  onClick,
  isAnchor = false,
  onMouseEnter,
  onMouseLeave,
  isPrivateDeal,
  hotelNameSlot,
  distanceSlot
}: Props) => {
  const {
    formatNumber
  } = useIntl();
  const dispatch = useDispatch();
  const isHotelVisited = useSelector(getIsHotelVisited(id));
  const isRtlLanguage = useSelector(getIsRtlLanguage);
  const currencyCode = useSelector(getCurrencyCode);
  const {
    brandShowSignIn
  } = useBrand();
  const roundedPrice = applyRoundingStrategy(getRoundingStrategy(), price);
  const formattedPrice = formatNumber(roundedPrice, {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  });
  const handleClickOnMarker = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    onClick(id);
    onMouseLeave?.(e);
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.MapViewHotelPin,
      action: Action.Clicked,
      analyticsContext: {
        [AnalyticsContext.HotelContext]: getParametrizedHotelContext(id),
        [AnalyticsContext.HotelInfoContext]: getParametrizedHotelInfoContext(id)
      }
    }));
  }, [id, onClick, dispatch, onMouseLeave]);
  const markerState = getMarkerState(isSelected, isHotelVisited);
  const hasPrice = price && price > 0;
  const textColor = getMarkerColor(markerState, isPrivateDeal, brandShowSignIn);
  const markerBorderColor = getMarkerBorderColor(markerState, isAnchor);
  const backgroundColor = getMarkerBackground(markerState, isPrivateDeal, brandShowSignIn);
  return <MarkerWrapper onMouseEnter={e => onMouseEnter?.(e)} onMouseLeave={e => onMouseLeave?.(e)}>
      <Marker data-id="MapPin" borderColor={markerBorderColor} backgroundColor={backgroundColor} textColor={textColor} isRTL={isRtlLanguage} onClick={handleClickOnMarker}>
        {hotelNameSlot && hotelNameSlot}
        <MarkerContent borderColor={markerBorderColor} backgroundColor={backgroundColor} textColor={textColor} isRTL={isRtlLanguage}>
          {hasPrice && <Price translate="no">{formattedPrice}</Price>}
          {!hasPrice && <IconWrapper isSelected={isSelected}>
              <Icon name="Forbid" size="sm" />
            </IconWrapper>}
        </MarkerContent>
        {distanceSlot && distanceSlot}
      </Marker>
    </MarkerWrapper>;
};

require("./HotelMarkerContent.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./HotelMarkerContent.tsx");